/**
 * Version Control API client for Phase 21.
 *
 * Provides functions for:
 * - Getting current version
 * - Managing changelog entries
 * - Version statistics
 */

import { api } from './api'

// =============================================================================
// Types
// =============================================================================

export type ChangeType = 'major' | 'minor' | 'patch'
export type ChangeCategory = 'feature' | 'bugfix' | 'security' | 'improvement' | 'breaking'

export interface VersionInfo {
  version: string
  environment: string
}

export interface ChangelogEntry {
  id: string
  version: string
  releaseDate: string
  changeType: ChangeType
  category: ChangeCategory
  title: string
  description: string | null
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

export interface ChangelogEntryCreate {
  version: string
  changeType: ChangeType
  category: ChangeCategory
  title: string
  description?: string | null
  releaseDate?: string | null
}

export interface ChangelogEntryUpdate {
  version?: string
  changeType?: ChangeType
  category?: ChangeCategory
  title?: string
  description?: string | null
  releaseDate?: string | null
}

export interface ChangelogEntryListResponse {
  data: ChangelogEntry[]
  total: number
  page: number
  pageSize: number
  hasMore: boolean
}

export interface VersionChangelogGroup {
  version: string
  releaseDate: string
  changeType: ChangeType
  entries: ChangelogEntry[]
}

export interface ChangelogByVersionResponse {
  currentVersion: string
  versions: VersionChangelogGroup[]
}

export interface ChangelogStats {
  totalVersions: number
  totalEntries: number
  latestVersion: string
  latestReleaseDate: string | null
  entriesByCategory: Record<string, number>
}

export interface VersionBumpResponse {
  oldVersion: string
  newVersion: string
  bumpType: ChangeType
  message: string
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get current platform version.
 */
export async function fetchCurrentVersion(): Promise<VersionInfo> {
  const response = await api.get<VersionInfo>('/admin/version')
  return response.data
}

/**
 * List changelog entries with pagination.
 */
export async function fetchChangelogEntries(
  page = 1,
  pageSize = 50,
  version?: string
): Promise<ChangelogEntryListResponse> {
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  })
  if (version) {
    params.append('version', version)
  }
  const response = await api.get<ChangelogEntryListResponse>(`/admin/version/changelog?${params}`)
  return response.data
}

/**
 * Get changelog entries grouped by version.
 */
export async function fetchChangelogGrouped(): Promise<ChangelogByVersionResponse> {
  const response = await api.get<ChangelogByVersionResponse>('/admin/version/changelog/grouped')
  return response.data
}

/**
 * Get a specific changelog entry.
 */
export async function fetchChangelogEntry(entryId: string): Promise<ChangelogEntry> {
  const response = await api.get<ChangelogEntry>(`/admin/version/changelog/${entryId}`)
  return response.data
}

/**
 * Create a new changelog entry.
 */
export async function createChangelogEntry(data: ChangelogEntryCreate): Promise<ChangelogEntry> {
  const response = await api.post<ChangelogEntry>('/admin/version/changelog', {
    version: data.version,
    change_type: data.changeType,
    category: data.category,
    title: data.title,
    description: data.description,
    release_date: data.releaseDate,
  })
  return response.data
}

/**
 * Update a changelog entry.
 */
export async function updateChangelogEntry(
  entryId: string,
  data: ChangelogEntryUpdate
): Promise<ChangelogEntry> {
  const payload: Record<string, unknown> = {}
  if (data.version !== undefined) payload.version = data.version
  if (data.changeType !== undefined) payload.change_type = data.changeType
  if (data.category !== undefined) payload.category = data.category
  if (data.title !== undefined) payload.title = data.title
  if (data.description !== undefined) payload.description = data.description
  if (data.releaseDate !== undefined) payload.release_date = data.releaseDate

  const response = await api.put<ChangelogEntry>(`/admin/version/changelog/${entryId}`, payload)
  return response.data
}

/**
 * Delete a changelog entry.
 */
export async function deleteChangelogEntry(entryId: string): Promise<void> {
  await api.delete(`/admin/version/changelog/${entryId}`)
}

/**
 * Get changelog statistics.
 */
export async function fetchChangelogStats(): Promise<ChangelogStats> {
  const response = await api.get<ChangelogStats>('/admin/version/stats')
  return response.data
}

/**
 * Preview version bump (does not make changes).
 */
export async function previewVersionBump(bumpType: ChangeType): Promise<VersionBumpResponse> {
  const response = await api.post<VersionBumpResponse>(
    `/admin/version/preview-bump?bump_type=${bumpType}`
  )
  return response.data
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get icon/emoji for change category.
 */
export function getCategoryIcon(category: ChangeCategory): string {
  const icons: Record<ChangeCategory, string> = {
    feature: '✨',
    bugfix: '🐛',
    security: '🔒',
    improvement: '🔧',
    breaking: '⚠️',
  }
  return icons[category] || '📝'
}

/**
 * Get display label for change category.
 */
export function getCategoryLabel(category: ChangeCategory): string {
  const labels: Record<ChangeCategory, string> = {
    feature: 'Feature',
    bugfix: 'Bug Fix',
    security: 'Security',
    improvement: 'Improvement',
    breaking: 'Breaking Change',
  }
  return labels[category] || category
}

/**
 * Get color class for change category badge.
 */
export function getCategoryColor(category: ChangeCategory): string {
  const colors: Record<ChangeCategory, string> = {
    feature: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    bugfix: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    security: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    improvement: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    breaking: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  }
  return colors[category] || 'bg-gray-100 text-gray-800'
}

/**
 * Get display label for change type.
 */
export function getChangeTypeLabel(changeType: ChangeType): string {
  const labels: Record<ChangeType, string> = {
    major: 'Major',
    minor: 'Minor',
    patch: 'Patch',
  }
  return labels[changeType] || changeType
}

/**
 * Calculate next version based on bump type.
 */
export function calculateNextVersion(current: string, bumpType: ChangeType): string {
  const parts = current.split('.')
  if (parts.length !== 3) return current

  const [major, minor, patch] = parts.map(Number)

  switch (bumpType) {
    case 'major':
      return `${major + 1}.0.0`
    case 'minor':
      return `${major}.${minor + 1}.0`
    case 'patch':
      return `${major}.${minor}.${patch + 1}`
    default:
      return current
  }
}
